import type { LatLngBoundsExpression, LatLngExpression, Map as LeafletMap } from 'leaflet';
import type { CSSProperties, MutableRefObject, ReactNode } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import { useSelector } from 'react-redux';

import BoundsSetter from 'components/BaseMap/components/BoundsSetter';
import LocationSetter from 'components/BaseMap/components/LocationSetter';
import ResetZoomMapEvent from 'components/BaseMap/components/ResetZoomMapEvent';
import ZoomOnFocusMapEvent from 'components/BaseMap/components/ZoomOnFocusMapEvent';
import { DEFAULT_LOCATION_COORDS } from 'config/defaultLocation.config.js';
import { selectMapOptions } from 'store/slices/DisplayOptions/displayOptionsSlice';
import { getStreetTileLayerConfig } from 'utils/maps/mapTile.utils';
import './assets/baseMap.css';
import AddCenterControlPositions from './components/AddCenterControlPositions';
import MapCrossDocumentInteractionFix from './components/MapCrossDocumentInteractionFix';
import MapInvalidateSizeFix from './components/MapInvalidateSizeFix';
import MapReset from './components/MapReset';
import MapView, { type MapViewContentProps } from './components/MapView';
import { stopLeafletMap } from './util/BaseMap.utils';

/** Supported map style identifiers used by the application theme settings. */
export type MapStyle =
  | 'default'
  | 'monochrome'
  | 'enhancedContrast'
  | 'highContrast'
  | 'defaultDark'
  | 'voyager'
  | 'voyagerDark'
  | 'dark';

/** Map display options used to resolve the street tile layer. */
export type BaseMapOptions = {
  /** Whether the selected style uses the dark map treatment. */
  darkMode: boolean;
  /** The configured street map style. */
  mapStyle: MapStyle;
};

/** Props for the shared Leaflet map wrapper. */
export type BaseMapProps = {
  /** Initial center of the map. */
  mapCenter?: LatLngExpression;
  /** Initial zoom level. */
  zoom?: number;
  /** Optional minimum zoom level. */
  minZoom?: number;
  /** Optional maximum zoom level. */
  maxZoom?: number;
  /** Optional bounds to fit and lock the user to a specific area. */
  bounds?: LatLngBoundsExpression | null;
  /**
   * Optional fit request for `bounds`. The map fits once for each new key. Without a key, the map
   * fits the first bounds once.
   */
  boundsFitKey?: string;
  /** Optional space, in pixels, between the fitted bounds and the map edge. */
  boundsPadding?: number;
  /** Optional panning fence without forcing a fit. */
  maxBounds?: LatLngBoundsExpression;
  /** Optional resistance when panning against `maxBounds`. */
  maxBoundsViscosity?: number;
  /** Enable or disable mouse-wheel zoom. */
  scrollZoom?: boolean;
  /** Optional custom street tile layer URL. */
  tileLayerUrl?: string | null;
  /** Disable automatic center and zoom synchronization. */
  disableLocationSync?: boolean;
  /** Disable the map reset control. */
  disableMapReset?: boolean;
  /** Preserve the current zoom when synchronizing the center. */
  preserveLocationZoom?: boolean;
  /** Ref that exposes the underlying Leaflet map instance. */
  mapRef?: MutableRefObject<LeafletMap | null> | null;
  /** Callback invoked when the underlying Leaflet map is ready. */
  onMapReady?: ((map: LeafletMap) => void) | null;
  /** Additional components in the map-view control stack. */
  extraStack?: ReactNode;
  /** Optional adjacent control rendered to the right of the map toggle. */
  mapViewAction?: ReactNode | ((props: MapViewContentProps) => ReactNode) | null;
  /** Optional expanded panel rendered beneath the map toggle row. */
  mapViewExpandedContent?: ReactNode | ((props: MapViewContentProps) => ReactNode) | null;
  /** Hide the labels toggle in the default map-view control. */
  mapViewHideLabelsToggle?: boolean;
  /** Prevent the map from repeating world copies horizontally. */
  disableWorldWrap?: boolean;
  /** Disable all default Leaflet map controls. */
  disableMapControls?: boolean;
  /** Enable or disable touch zoom. */
  touchZoom?: boolean;
  /** Additional classes for the map container. */
  className?: string;
  /** Additional styles for the map container. */
  style?: CSSProperties;
  /** Optional local map style override. */
  mapOptions?: BaseMapOptions | null;
  /** Additional children rendered on top of the map. */
  children?: ReactNode;
} & {};

/**
 * Render a Leaflet map with themed tiles, synchronization, and shared controls.
 *
 * @param props Map view, tile layer, synchronization, control, and child content configuration.
 * @returns The configured Leaflet map container.
 */
const BaseMap = ({
  mapCenter = [...DEFAULT_LOCATION_COORDS] as [number, number],
  zoom = 5,
  minZoom,
  maxZoom,
  bounds,
  boundsFitKey,
  boundsPadding,
  maxBounds,
  maxBoundsViscosity,
  scrollZoom = true,
  tileLayerUrl = null,
  disableLocationSync = false,
  disableMapReset = false,
  preserveLocationZoom = false,
  mapRef = null,
  onMapReady = null,
  extraStack,
  mapViewAction = null,
  mapViewExpandedContent = null,
  mapViewHideLabelsToggle = false,
  disableWorldWrap = false,
  disableMapControls = false,
  touchZoom = true,
  className = '',
  style = {},
  mapOptions = null,
  children,
}: BaseMapProps): JSX.Element => {
  const reduxMapOptions = useSelector(selectMapOptions);
  const resolvedMapOptions = mapOptions ?? reduxMapOptions;
  const STREET_MAX_NATIVE_ZOOM = 19;
  const SATELLITE_MAX_NATIVE_ZOOM = 19;
  const resolvedTileMaxZoom = Number.isFinite(maxZoom) ? maxZoom : 23;
  const streetTileLayer = getStreetTileLayerConfig(resolvedMapOptions);

  const [interacted, setInteracted] = useState(false);
  const [initPos, setInitPos] = useState(mapCenter);
  const [initZoom, setInitZoom] = useState(zoom);
  const [resetTrigger, setResetTrigger] = useState<Date | null>(null);
  const [satelliteView, setSatelliteView] = useState(false);
  const [labelsEnabled, setLabelsEnabled] = useState(true);
  const internalMapRef = useRef<LeafletMap | null>(null);
  const resolvedMapRef = mapRef && typeof mapRef === 'object' ? mapRef : internalMapRef;
  const resolvedClassName = [streetTileLayer.containerClassName, 'rounded-2', className]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    if (JSON.stringify(initPos) !== JSON.stringify(mapCenter)) {
      setInitPos(mapCenter);
    }
  }, [mapCenter, initPos]);

  useEffect(() => {
    if (initZoom !== zoom) {
      setInitZoom(zoom);
    }
  }, [zoom, initZoom]);

  const handleMapReady = useCallback(() => {
    if (typeof onMapReady !== 'function') return;
    const instance = resolvedMapRef.current;
    if (instance) {
      onMapReady(instance);
    }
  }, [onMapReady, resolvedMapRef]);

  useEffect(() => {
    return () => {
      stopLeafletMap(resolvedMapRef.current);
      resolvedMapRef.current = null;
    };
  }, [resolvedMapRef]);

  const handleResetClick = () => {
    setInteracted(false);
    setResetTrigger(new Date());
  };

  return (
    <MapContainer
      ref={resolvedMapRef}
      className={resolvedClassName}
      zoomControl={!disableMapControls}
      center={mapCenter}
      zoom={zoom}
      minZoom={minZoom}
      maxZoom={maxZoom}
      dragging={!disableMapControls}
      doubleClickZoom={false}
      maxBounds={maxBounds}
      maxBoundsViscosity={maxBoundsViscosity}
      scrollWheelZoom={scrollZoom}
      touchZoom={touchZoom}
      worldCopyJump={!disableWorldWrap}
      style={{ width: '100%', height: '100%', ...style }}
      whenReady={handleMapReady}
    >
      <MapInvalidateSizeFix />
      <MapCrossDocumentInteractionFix />
      <AddCenterControlPositions />
      <TileLayer
        url={
          !satelliteView
            ? tileLayerUrl || streetTileLayer.url
            : 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
        }
        attribution={!satelliteView && !tileLayerUrl ? streetTileLayer.attribution : undefined}
        className={!satelliteView && !tileLayerUrl ? streetTileLayer.className : undefined}
        maxZoom={resolvedTileMaxZoom}
        maxNativeZoom={satelliteView ? SATELLITE_MAX_NATIVE_ZOOM : STREET_MAX_NATIVE_ZOOM}
        noWrap={disableWorldWrap}
      />
      {labelsEnabled && satelliteView && (
        <TileLayer
          url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
          maxZoom={resolvedTileMaxZoom}
          maxNativeZoom={SATELLITE_MAX_NATIVE_ZOOM}
          noWrap={disableWorldWrap}
        />
      )}
      <ZoomOnFocusMapEvent setInteracted={setInteracted} />
      <ResetZoomMapEvent
        setInitPos={setInitPos}
        setInitZoom={setInitZoom}
        triggerReset={resetTrigger}
        initPos={initPos}
        initZoom={initZoom}
      />
      {!disableMapReset && <MapReset onClick={handleResetClick} interacted={interacted} />}
      {!disableMapControls && (
        <MapView
          satelliteView={satelliteView}
          labelsEnabled={labelsEnabled}
          onToggleSatellite={() => setSatelliteView((prev) => !prev)}
          onToggleLabels={() => setLabelsEnabled((prev) => !prev)}
          extra={extraStack}
          actionControl={mapViewAction}
          expandedContent={mapViewExpandedContent}
          hideLabelsToggle={mapViewHideLabelsToggle}
        />
      )}
      {!disableLocationSync && (
        <LocationSetter center={mapCenter} zoom={zoom} preserveZoom={preserveLocationZoom} />
      )}
      <BoundsSetter bounds={bounds} fitKey={boundsFitKey} padding={boundsPadding} />
      {children}
    </MapContainer>
  );
};

export default BaseMap;
